import { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import Icon from '@dustin-web/microsite-components/src/icon'
import classNames from 'classnames'
import {
  useResponsive,
  useSiteContext,
  useTranslations,
  useUserSettings,
} from '@dustin-web/microsite-components/src/hooks'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'

const Spinner = dynamic(() => import('@dustin-web/microsite-components/src/spinner'))

declare global {
  interface Window {
    zE: (action: string, ...args: unknown[]) => void
  }
}

declare const zE: (action: string, ...args: unknown[]) => void

const ZD_SESSION_KEY = 'ZD-buid'

const envList = ['test', 'local', 'staging']

const chatKeySet: Record<string, string> = {
  SE: '3971b4ad-0a44-4027-b0b4-e2fc1ecc8bad',
  DK: '14aaca48-da82-4f4f-b8ea-a66c7d6b2945',
  NO: 'f9846ef2-78c6-494d-89bc-b9000dda71ed',
  FI: '05d28232-10a7-4af4-b971-6e9f8626d1f2',
  NL: 'c17ed772-8754-48d6-b9b7-ea282f6e0775',
  SANDBOX: 'be70ff64-6d4f-4003-a9bc-8e6dc0ff8ae8',
} as const

const excludedStaticPages = ['/shoppingcart', '/checkoutflow', '/checkoutsite', '/brands']
const excludedPages = ['/']
const excludeFilterPage = 'group'

// Upper bound for how long the open path waits for an authenticated user's contact
// metadata to hydrate before opening the messenger anyway. Contact data can genuinely
// never arrive, so this timeout is the hard bound that guarantees the open path ends.
const METADATA_WAIT_TIMEOUT_MS = 8000
const METADATA_POLL_INTERVAL_MS = 500

const RenderZendeskChat = ({ path }: { path: string }) => {
  const { isSmallScreen } = useResponsive()
  const { twoLetterISOLanguageName } = useUserSettings()
  const { sourcingCompany, serverEnv } = useSiteContext()
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [loadingScript, setLoadingScript] = useState(false)

  const unreadMessages = useRef<HTMLSpanElement | null>(null)

  const { translate } = useTranslations()
  const { getZendeskToken, isAuthenticated, isLoading, userData } = useAuth()
  const { enabled: zendeskAuthToggle } = useFeatureFlag('enable-zendesk-auth')
  const customerErp = userData?.contact?.customerErp
  const customerId = userData?.contact?.customerId
  const contactId = userData?.contact?.id

  // The bounded wait on the open path runs inside an async function while auth state
  // arrives via React state. This ref holds the latest snapshot so the polling loop
  // reads fresh values instead of a stale closure captured when the loop started.
  const authSnapshot = useRef({ isLoading, isAuthenticated, customerErp, customerId, contactId })
  authSnapshot.current = { isLoading, isAuthenticated, customerErp, customerId, contactId }

  // Tracks intervals started by waitUntil so they can be cleared if the component
  // unmounts mid-wait, preventing orphaned timers from resolving on an unmounted
  // component.
  const pollIntervals = useRef<Set<number>>(new Set())

  useEffect(() => {
    const resumeChat = localStorage.getItem(ZD_SESSION_KEY)

    if (!!resumeChat && !scriptLoaded) {
      loadZendesk(false)
    }

    window.addEventListener('openZendeskChat', openChat)

    return () => {
      window.removeEventListener('openZendeskChat', openChat)
    }
  })

  // Unmount-only cleanup: clear any polling intervals still running from waitUntil.
  // Kept in its own effect with an empty dependency array so its cleanup runs solely
  // on unmount — the listener effect above has no deps and re-runs every render, which
  // would otherwise clear an in-flight wait on a routine re-render.
  useEffect(() => {
    const intervals = pollIntervals.current

    return () => {
      intervals.forEach(id => window.clearInterval(id))
      intervals.clear()
    }
  }, [])

  const lang = twoLetterISOLanguageName === 'nb' ? 'no' : twoLetterISOLanguageName
  const chatKey = chatKeySet[envList.includes(serverEnv) ? 'SANDBOX' : sourcingCompany]
  const url = typeof document !== 'undefined' && document.URL
  const params = path.split('/')

  const disableFilterPage = params[1] === excludeFilterPage && params.length > 4
  const isPageDisabled = excludedPages.includes(path)
  const isStaticPage = excludedStaticPages.some(str => path.includes(str))
  const hideChatButton = isStaticPage || isPageDisabled || disableFilterPage

  // Reads the current auth snapshot and pushes the conversation fields to Zendesk.
  // Called before the messenger is opened so the conversation is created with the
  // correct customer metadata (empty for guests, populated for authenticated users).
  const setConversationFields = () => {
    const { customerErp: erp, customerId: custId, contactId: cId } = authSnapshot.current

    zE('messenger:set', 'conversationFields', [
      { id: '25235101622929', value: url },
      { id: '28467280679953', value: lang },
      { id: '39341791419793', value: erp },
      { id: '43051645717009', value: cId },
      { id: '43051686027921', value: custId },
    ])
  }

  // Whether the authenticated user's contact metadata has hydrated.
  const hasContactMetadata = () => {
    const { contactId: cId } = authSnapshot.current
    return cId !== undefined && cId !== null
  }

  // Polls the auth snapshot until `predicate` is satisfied. Resolves early once it is.
  // When `timeoutMs` is provided, also resolves after that bound elapses; when omitted,
  // waits indefinitely (used while auth state is still resolving). The interval is
  // registered in pollIntervals so it is cleared if the component unmounts mid-wait.
  const waitUntil = (predicate: () => boolean, timeoutMs?: number) =>
    new Promise<void>(resolve => {
      if (predicate()) {
        resolve()
        return
      }

      const startedAt = Date.now()
      const intervalId = window.setInterval(() => {
        const timedOut = timeoutMs !== undefined && Date.now() - startedAt >= timeoutMs

        if (predicate() || timedOut) {
          window.clearInterval(intervalId)
          pollIntervals.current.delete(intervalId)
          resolve()
        }
      }, METADATA_POLL_INTERVAL_MS)

      pollIntervals.current.add(intervalId)
    })

  // Waits until it is safe to open the messenger with the correct customer state.
  // Two distinct stages, matching how the header resolves guest vs. logged-in:
  //   1. Wait for auth to settle (isLoading === false). No timeout: while auth is
  //      unresolved we cannot tell a guest from a logged-in user, so we must not open
  //      and must not guess.
  //   2. Once settled, a guest is ready immediately. An authenticated user whose
  //      contact metadata has not hydrated is waited on for up to
  //      METADATA_WAIT_TIMEOUT_MS, then opened anyway — contact data can genuinely
  //      never arrive, so this bound guarantees the open path completes.
  const waitForMetadata = async () => {
    await waitUntil(() => !authSnapshot.current.isLoading)

    if (!authSnapshot.current.isAuthenticated) {
      return
    }

    await waitUntil(hasContactMetadata, METADATA_WAIT_TIMEOUT_MS)
  }

  // Applies the user's authentication status to the Zendesk messenger. Must run before
  // the messenger is opened so an already-logged-in user is not shown the login prompt.
  // Reads the live auth snapshot (not the render closure) because it runs after an
  // async wait during which the component may have re-rendered. No-op when the feature
  // flag is off. The `isLoading` guard is defensive only: callers (openMessenger) wait
  // for auth to settle first, so in practice auth is already resolved here.
  const authenticateMessenger = async () => {
    const { isLoading: loading, isAuthenticated: authed } = authSnapshot.current

    if (!zendeskAuthToggle || loading) {
      return
    }

    try {
      if (authed) {
        const token = await getZendeskToken()
        if (token) {
          zE('messenger', 'loginUser', (callback: any) => callback(token))
        }
      } else {
        zE('messenger', 'logoutUser')
      }
    } catch (error) {
      console.error(error)
    }
  }

  // Opens the messenger only once the customer metadata is ready. Zendesk creates the
  // conversation (and emits its "please log in" bot message) on open, so the metadata
  // and authentication must be applied first. For an authenticated user whose contact
  // data is late, this waits up to METADATA_WAIT_TIMEOUT_MS; on timeout it opens anyway
  // with whatever is available, so the open path always completes. Guests fall through
  // the wait immediately and open with empty fields, unchanged.
  const openMessenger = async () => {
    try {
      await waitForMetadata()

      setConversationFields()

      await authenticateMessenger()
    } catch (error) {
      // Never leave the user with a chat that won't open: log and fall through to open
      // regardless, degrading to the default (guest-style) experience.
      console.error(error)
    }

    window.zE('messenger', 'open')
  }

  const initializeZendesk = async (openChat: boolean) => {
    // Initialize Zendesk settings after the script is loaded
    // allows support to pickup selected lang in browser, because english is not supported yet.
    zE('messenger:set', 'locale', lang)
    zE('messenger', 'close')

    if (openChat) {
      await openMessenger()
    }

    setTimeout(() => {
      setLoadingScript(false)
    }, 500)

    const conversationBadge = document.querySelector('#conversation-badge') as HTMLElement

    const populateUnreadIndicator = (count: number) => {
      if (count > 0) {
        const { current } = unreadMessages || {}

        if (!current) {
          return
        }

        current.style.background = '#CC3333'
        current.innerHTML = count.toString()
        current.classList.add('tilt-animation')
      } else {
        resetUnreadIndicator()
      }
    }

    const resetUnreadIndicator = () => {
      const { current } = unreadMessages || {}

      if (!current) {
        return
      }

      current.style.background = 'gray'
      current.innerHTML = '0'
      conversationBadge.classList.remove('tilt-animation')
    }

    setTimeout(() => {
      zE('messenger:on', 'unreadMessages', (count: number) => {
        populateUnreadIndicator(count)
      })
    }, 1000)

    resetUnreadIndicator()
  }

  const loadZendesk = (openChat = true) => {
    if (!chatKey) {
      console.error(`Error: zendesk chatkey is undefined for ${lang} market`)
      return
    }

    const script = document.createElement('script')

    script.id = 'ze-snippet'
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${chatKey}`
    script.onload = () => {
      initializeZendesk(openChat).catch(error => console.error(error))
    }

    document.body.appendChild(script)

    setScriptLoaded(true)
    setLoadingScript(true)
  }

  const openChat: any = async () => {
    if (!scriptLoaded) {
      loadZendesk()
      return
    }

    await openMessenger()
  }

  return (
    <div
      className={classNames('zendesk-chat u-layer-modal', {
        'd-none': hideChatButton,
        mobile: isSmallScreen,
      })}>
      <button
        id="conversation-badge"
        aria-label={translate('Aria_Label_Open_Zendesk_Support')}
        onClick={openChat}>
        {!loadingScript ? (
          <div className="d-flex flex-column">
            <Icon name="speech-bubble-2" className="i-24" />
            <span style={{ fontSize: '10px', fontWeight: 700 }}>
              {translate('Chat_Button_Text')}
            </span>
          </div>
        ) : (
          <Spinner local size="xxs" />
        )}
      </button>
      {scriptLoaded && !loadingScript && (
        <span ref={unreadMessages} id="unread-indicator">
          0
        </span>
      )}
    </div>
  )
}

export default RenderZendeskChat
